// Canonical supplier onboarding outcome contract (ENT-1396).
//
// This module is the single source of truth for:
//   * reason codes for the seven onboarding failure/incomplete classes
//     observed in the 2026-05-15 13:00 UTC batch (ENT-1393);
//   * lifecycle states that describe where a marketplace listing sits in
//     onboarding diagnostics;
//   * repair states that describe whether automation or a human must act;
//   * score/evidence reconciliation states that describe whether the
//     canonical SupplierLens score is consistent with the evidence on
//     record.
//
// Backend writes, admin UX, customer UX, batch reporting, alerting, and
// repair/retry automation MUST import these constants/types. Defining a
// per-surface enum, status string, or fallback label is a contract break
// (ENT-1393 canonical logic requirement).
//
// Unknown / unmapped values fail closed via the parse* helpers. New
// outcomes must be added here first and only then encoded by callers.

// -------------------------------------------------------------------------
// Reason codes
// -------------------------------------------------------------------------

export const SUPPLIER_ONBOARDING_SUCCEEDED_REASON_CODE =
  "onboarding_succeeded";

export const SUPPLIER_ONBOARDING_CANONICAL_URL_MANUAL_REVIEW_REASON_CODE =
  "canonical_url_manual_review";
export const SUPPLIER_ONBOARDING_DELETED_AFTER_ONBOARDING_REASON_CODE =
  "deleted_after_onboarding";
export const SUPPLIER_ONBOARDING_EVIDENCE_SCORE_GAP_REASON_CODE =
  "evidence_score_gap";
export const SUPPLIER_ONBOARDING_COMPONENT_RETRY_EXHAUSTED_REASON_CODE =
  "component_retry_exhausted";
export const SUPPLIER_ONBOARDING_PROVIDER_RETRY_EXHAUSTED_REASON_CODE =
  "provider_retry_exhausted";
export const SUPPLIER_ONBOARDING_DUPLICATE_ORG_LINK_REASON_CODE =
  "duplicate_org_link";
export const SUPPLIER_ONBOARDING_TERMINAL_DNS_NO_HOST_REASON_CODE =
  "terminal_dns_no_host";
// ENT-2464: a domain with a live zone (NS) + MX but no A/AAAA record on the
// apex OR www — a real business that reads mail but publishes no website.
// Distinct from terminal_dns_no_host (which is a genuinely dead/unregistered
// zone): non-terminal, hands off to the operator for a mail-only onboard, and
// the web scan components are marked Not Applicable rather than scored as fail.
export const SUPPLIER_ONBOARDING_NO_PUBLIC_WEBSITE_REASON_CODE =
  "no_public_website";

export const SUPPLIER_ONBOARDING_REASON_CODES = [
  // 151 cases in the 195-row batch — supplier onboarding completed
  // successfully with aligned score/evidence and no repair needed.
  // The contract uses a positive reason code (not a NULL/absence) so
  // every marketplace listing carries a canonical row regardless of
  // outcome, keeping read-models and reports uniform.
  SUPPLIER_ONBOARDING_SUCCEEDED_REASON_CODE,

  // 23 cases in batch — supplier was successfully onboarded then deleted /
  // archived. Listing must not surface as a live failure.
  SUPPLIER_ONBOARDING_DELETED_AFTER_ONBOARDING_REASON_CODE,

  // 10 cases — onboarding completed but required component evidence or
  // score components are missing; reconciliation is needed before the
  // listing can be treated as fully scored.
  SUPPLIER_ONBOARDING_EVIDENCE_SCORE_GAP_REASON_CODE,

  // 4waysolutions in batch — at least one required scan component has
  // exceeded retry budget and the canonical score snapshot is stale until
  // the component is repaired.
  SUPPLIER_ONBOARDING_COMPONENT_RETRY_EXHAUSTED_REASON_CODE,

  // ionet.ie, novemgroup.co.uk in batch — upstream provider (e.g.
  // Firecrawl) retry budget exhausted on a required scan, distinct from a
  // single-component exhaustion because the blocker is the provider.
  SUPPLIER_ONBOARDING_PROVIDER_RETRY_EXHAUSTED_REASON_CODE,

  // wirral.gov.uk in batch — the supplier domain resolves to an org link
  // that already exists for another tenant/listing. Manual reconciliation
  // is required to avoid duplicate organisation records.
  SUPPLIER_ONBOARDING_DUPLICATE_ORG_LINK_REASON_CODE,

  // 4 cases — DNS resolution permanently fails or the host does not
  // exist. Automated retry must not re-queue this without human action.
  SUPPLIER_ONBOARDING_TERMINAL_DNS_NO_HOST_REASON_CODE,

  // 3 cases — the submitted URL is not the canonical surface for the
  // organisation (e.g. tracking shortlink, parked page, marketing
  // microsite). A human must confirm the canonical URL before scanning.
  SUPPLIER_ONBOARDING_CANONICAL_URL_MANUAL_REVIEW_REASON_CODE,

  // ENT-2464 — live zone (NS) + MX, no A/AAAA record: a mail-only business.
  // Not dead: onboard it (operator-confirmed identity) and mark the web
  // scan components Not Applicable. Non-terminal, requires manual review.
  SUPPLIER_ONBOARDING_NO_PUBLIC_WEBSITE_REASON_CODE,
] as const;

export type SupplierOnboardingReasonCode =
  typeof SUPPLIER_ONBOARDING_REASON_CODES[number];

export const SUPPLIER_ONBOARDING_TERMINAL_DNS_REASON_CODE =
  SUPPLIER_ONBOARDING_TERMINAL_DNS_NO_HOST_REASON_CODE;

// -------------------------------------------------------------------------
// Lifecycle states
// -------------------------------------------------------------------------

export const SUPPLIER_ONBOARDING_LIFECYCLE_STATES = [
  "pending",
  "in_progress",
  "succeeded",
  "incomplete",
  "failed_retryable",
  "failed_terminal",
  "requires_manual_review",
  "archived_after_onboarding",
] as const;

export type SupplierOnboardingLifecycleState =
  typeof SUPPLIER_ONBOARDING_LIFECYCLE_STATES[number];

// -------------------------------------------------------------------------
// Repair states
// -------------------------------------------------------------------------

export const SUPPLIER_ONBOARDING_REPAIR_STATES = [
  "none",
  "auto_retry_scheduled",
  "auto_retry_in_progress",
  "manual_action_required",
  "repair_blocked",
  "repair_complete",
] as const;

export type SupplierOnboardingRepairState =
  typeof SUPPLIER_ONBOARDING_REPAIR_STATES[number];

// -------------------------------------------------------------------------
// Score / evidence reconciliation states
// -------------------------------------------------------------------------

export const SUPPLIER_ONBOARDING_RECONCILIATION_STATES = [
  "aligned",
  "evidence_missing",
  "score_stale",
  "score_recompute_required",
  "unverifiable",
] as const;

export type SupplierOnboardingReconciliationState =
  typeof SUPPLIER_ONBOARDING_RECONCILIATION_STATES[number];

// -------------------------------------------------------------------------
// Canonical default mapping per reason code.
//
// This is the canonical decision table: given a reason code, what
// lifecycle / repair / reconciliation defaults are correct? Surfaces and
// automation may override only with explicit evidence — they MUST NOT
// invent a different default for the same reason code.
// -------------------------------------------------------------------------

export interface SupplierOnboardingOutcomeDefaults {
  lifecycle: SupplierOnboardingLifecycleState;
  repair: SupplierOnboardingRepairState;
  reconciliation: SupplierOnboardingReconciliationState;
  terminal: boolean;
  retryEligible: boolean;
}

// Frozen so consumers cannot accidentally mutate the canonical defaults
// for a single call site (e.g. flipping `retryEligible` after lookup)
// and silently corrupt every subsequent call's mapping.
const DEFAULTS: Readonly<
  Record<SupplierOnboardingReasonCode, Readonly<SupplierOnboardingOutcomeDefaults>>
> = Object.freeze({
  onboarding_succeeded: Object.freeze({
    lifecycle: "succeeded",
    repair: "none",
    reconciliation: "aligned",
    terminal: true,
    retryEligible: false,
  }),
  deleted_after_onboarding: Object.freeze({
    lifecycle: "archived_after_onboarding",
    repair: "none",
    reconciliation: "unverifiable",
    terminal: true,
    retryEligible: false,
  }),
  evidence_score_gap: Object.freeze({
    lifecycle: "incomplete",
    repair: "auto_retry_scheduled",
    reconciliation: "evidence_missing",
    terminal: false,
    retryEligible: true,
  }),
  component_retry_exhausted: Object.freeze({
    lifecycle: "failed_retryable",
    repair: "manual_action_required",
    reconciliation: "score_stale",
    terminal: false,
    retryEligible: true,
  }),
  provider_retry_exhausted: Object.freeze({
    lifecycle: "failed_retryable",
    repair: "auto_retry_scheduled",
    reconciliation: "score_recompute_required",
    terminal: false,
    retryEligible: true,
  }),
  duplicate_org_link: Object.freeze({
    lifecycle: "requires_manual_review",
    repair: "manual_action_required",
    reconciliation: "unverifiable",
    terminal: false,
    retryEligible: false,
  }),
  terminal_dns_no_host: Object.freeze({
    lifecycle: "failed_terminal",
    repair: "repair_blocked",
    reconciliation: "unverifiable",
    terminal: true,
    retryEligible: false,
  }),
  canonical_url_manual_review: Object.freeze({
    lifecycle: "requires_manual_review",
    repair: "manual_action_required",
    reconciliation: "unverifiable",
    terminal: false,
    retryEligible: false,
  }),
  // ENT-2464: mail-only domain. Non-terminal — the supplier is real and the
  // operator can onboard it (mail-only) once identity is confirmed. Not
  // retry-eligible: re-running website discovery cannot conjure a web host, so
  // the discovery queue must NOT re-queue it (it settles like the manual-review
  // classes). Mirrors canonical_url_manual_review's shape.
  no_public_website: Object.freeze({
    lifecycle: "requires_manual_review",
    repair: "manual_action_required",
    reconciliation: "unverifiable",
    terminal: false,
    retryEligible: false,
  }),
});

// Returns a fresh copy so callers can adjust per-call fields (e.g. an
// override `retryEligible` for a one-off decision) without mutating the
// shared canonical defaults.
export function defaultsForReasonCode(
  reason: SupplierOnboardingReasonCode,
): SupplierOnboardingOutcomeDefaults {
  return { ...DEFAULTS[reason] };
}

// -------------------------------------------------------------------------
// Fail-closed parsers
//
// Callers persisting or reading outcome values from untrusted sources
// (DB row, RPC payload, external API) MUST route through these helpers.
// They throw a typed error on unknown values rather than silently
// degrading to a generic label.
// -------------------------------------------------------------------------

export class UnknownSupplierOnboardingOutcomeValueError extends Error {
  readonly field:
    | "reason_code"
    | "lifecycle_state"
    | "repair_state"
    | "reconciliation_state";
  readonly received: unknown;

  constructor(
    field: UnknownSupplierOnboardingOutcomeValueError["field"],
    received: unknown,
  ) {
    super(
      `Unknown supplier onboarding ${field} value: ${JSON.stringify(received)}. ` +
        `Add it to supplier-onboarding-outcome.ts (ENT-1396) before persisting or rendering it.`,
    );
    this.name = "UnknownSupplierOnboardingOutcomeValueError";
    this.field = field;
    this.received = received;
  }
}

function parseEnum<T extends string>(
  field: UnknownSupplierOnboardingOutcomeValueError["field"],
  allowed: readonly T[],
  value: unknown,
): T {
  if (typeof value === "string" && (allowed as readonly string[]).includes(value)) {
    return value as T;
  }
  throw new UnknownSupplierOnboardingOutcomeValueError(field, value);
}

export function parseReasonCode(value: unknown): SupplierOnboardingReasonCode {
  return parseEnum("reason_code", SUPPLIER_ONBOARDING_REASON_CODES, value);
}

export function parseLifecycleState(
  value: unknown,
): SupplierOnboardingLifecycleState {
  return parseEnum(
    "lifecycle_state",
    SUPPLIER_ONBOARDING_LIFECYCLE_STATES,
    value,
  );
}

export function parseRepairState(
  value: unknown,
): SupplierOnboardingRepairState {
  return parseEnum("repair_state", SUPPLIER_ONBOARDING_REPAIR_STATES, value);
}

export function parseReconciliationState(
  value: unknown,
): SupplierOnboardingReconciliationState {
  return parseEnum(
    "reconciliation_state",
    SUPPLIER_ONBOARDING_RECONCILIATION_STATES,
    value,
  );
}

// -------------------------------------------------------------------------
// Canonical outcome row (DB / read-model shape).
//
// Persisted in public.supplier_onboarding_outcomes and exposed by the
// admin and customer read models. Surfaces must consume this shape; they
// must not project alternative status fields from raw discovery / scan
// tables for the same data.
// -------------------------------------------------------------------------

export interface SupplierOnboardingOutcomeRow {
  marketplace_listing_id: string;
  reason_code: SupplierOnboardingReasonCode;
  lifecycle_state: SupplierOnboardingLifecycleState;
  repair_state: SupplierOnboardingRepairState;
  reconciliation_state: SupplierOnboardingReconciliationState;
  terminal: boolean;
  retry_eligible: boolean;
  next_action: string | null;
  reason_detail: Record<string, unknown> | null;
  last_evaluated_at: string;
  last_repair_attempt_at: string | null;
}

export function buildOutcomeRow(input: {
  marketplaceListingId: string;
  reasonCode: SupplierOnboardingReasonCode;
  lifecycleState?: SupplierOnboardingLifecycleState;
  repairState?: SupplierOnboardingRepairState;
  reconciliationState?: SupplierOnboardingReconciliationState;
  nextAction?: string | null;
  reasonDetail?: Record<string, unknown> | null;
  lastEvaluatedAt: string;
  lastRepairAttemptAt?: string | null;
}): SupplierOnboardingOutcomeRow {
  const defaults = DEFAULTS[input.reasonCode];
  const lifecycle = input.lifecycleState ?? defaults.lifecycle;
  const repair = input.repairState ?? defaults.repair;
  const reconciliation = input.reconciliationState ?? defaults.reconciliation;
  return {
    marketplace_listing_id: input.marketplaceListingId,
    reason_code: input.reasonCode,
    lifecycle_state: lifecycle,
    repair_state: repair,
    reconciliation_state: reconciliation,
    terminal: defaults.terminal,
    retry_eligible: defaults.retryEligible &&
      repair !== "repair_blocked" &&
      repair !== "repair_complete",
    next_action: input.nextAction ?? null,
    reason_detail: input.reasonDetail ?? null,
    last_evaluated_at: input.lastEvaluatedAt,
    last_repair_attempt_at: input.lastRepairAttemptAt ?? null,
  };
}

// -------------------------------------------------------------------------
// ENT-5456: "Mark dead" review decision
// -------------------------------------------------------------------------
// resolve_onboarding_review's mark_dead branch has no reason code of its own:
// it stores reason_code=terminal_dns_no_host. A listing outcome also carries
// next_action=marked_dead_by_operator; a pre-listing discovery-queue row has no
// next_action column and carries only reason_detail.decision='mark_dead'. Both
// record reason_detail.{review_id, resolved_by, resolved_at}. The review gates
// (process-onboarding-review-queue) resolve automatically as the fixed system
// actor, so resolved_by tells an operator kill from a review-automation kill.
// Every admin surface that renders the reason code MUST check this first and
// name the decision (who / when / review case) instead of presenting the
// stored DNS code as a DNS verdict. The stored code itself stays visible,
// labelled. A dedicated reason code is ENT-5464 (needs $sg-analysis first).
export const SUPPLIER_ONBOARDING_MARKED_DEAD_BY_OPERATOR_NEXT_ACTION =
  "marked_dead_by_operator";
export const SUPPLIER_ONBOARDING_MARKED_DEAD_BY_OPERATOR_LABEL =
  "Marked dead by operator";
export const SUPPLIER_ONBOARDING_MARKED_DEAD_BY_AUTOMATION_LABEL =
  "Marked dead by review automation";
// A projection without reason_detail.resolved_by cannot say who decided; it is
// never credited to an operator (Codex P2, PR #3504).
export const SUPPLIER_ONBOARDING_MARKED_DEAD_UNATTRIBUTED_LABEL =
  "Marked dead by review decision";
// The actor resolve_onboarding_review records for automatic resolutions.
export const SUPPLIER_ONBOARDING_SYSTEM_ACTOR_UUID =
  "00000000-0000-0000-0000-000000000000";

export type MarkedDeadDecisionSource = "operator" | "automation" | "unattributed";

export function markedDeadDecisionSource(
  row:
    | {
      reason_code?: string | null;
      next_action?: string | null;
      reason_detail?: unknown;
    }
    | null
    | undefined,
): MarkedDeadDecisionSource | null {
  if (row?.reason_code !== SUPPLIER_ONBOARDING_TERMINAL_DNS_NO_HOST_REASON_CODE) {
    return null;
  }
  const detail: Record<string, unknown> =
    row.reason_detail && typeof row.reason_detail === "object" &&
      !Array.isArray(row.reason_detail)
      ? row.reason_detail as Record<string, unknown>
      : {};
  const nextAction = typeof row.next_action === "string"
    ? row.next_action.trim()
    : "";
  const markedDead = nextAction
    ? nextAction === SUPPLIER_ONBOARDING_MARKED_DEAD_BY_OPERATOR_NEXT_ACTION
    : detail.decision === "mark_dead";
  if (!markedDead) return null;
  const resolvedBy = typeof detail.resolved_by === "string"
    ? detail.resolved_by.trim()
    : "";
  if (!resolvedBy) return "unattributed";
  return resolvedBy === SUPPLIER_ONBOARDING_SYSTEM_ACTOR_UUID
    ? "automation"
    : "operator";
}

/** True for any Mark dead review decision (operator or review automation). */
export function isMarkedDeadDecisionOutcome(
  row: Parameters<typeof markedDeadDecisionSource>[0],
): boolean {
  return markedDeadDecisionSource(row) !== null;
}

/** True only when a human operator made the Mark dead decision. */
export function isOperatorMarkedDeadOutcome(
  row: Parameters<typeof markedDeadDecisionSource>[0],
): boolean {
  return markedDeadDecisionSource(row) === "operator";
}

export function markedDeadDecisionLabel(
  source: MarkedDeadDecisionSource,
): string {
  if (source === "automation") {
    return SUPPLIER_ONBOARDING_MARKED_DEAD_BY_AUTOMATION_LABEL;
  }
  return source === "operator"
    ? SUPPLIER_ONBOARDING_MARKED_DEAD_BY_OPERATOR_LABEL
    : SUPPLIER_ONBOARDING_MARKED_DEAD_UNATTRIBUTED_LABEL;
}
